/**
 * Labstep
 *
 * @module containers/ActiveGroup
 * @desc Container for ui
 */

import {
  selectActiveGroup,
  selectActiveGroupId,
  setActiveGroupId,
} from 'labstep-web/state/slices/activeGroup';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { WithActiveGroupInjectedProps } from './types';

// Modern hooks-based HOC
export const withActiveGroup = <
  P extends WithActiveGroupInjectedProps,
  S,
>(
  Component: React.FC<P> | React.ComponentClass<P, S>,
): React.FC<
  Pick<P, Exclude<keyof P, keyof WithActiveGroupInjectedProps>>
> => {
  const WrappedComponent: React.FC<
    Pick<P, Exclude<keyof P, keyof WithActiveGroupInjectedProps>>
  > = React.memo((props) => {
    const dispatch = useDispatch();
    const activeGroup = useSelector(selectActiveGroup);
    const activeGroupId = useSelector(selectActiveGroupId);

    const setActiveGroupIdMemo = useCallback(
      (id: number) => dispatch(setActiveGroupId(id)),
      [dispatch],
    );

    const injectedProps = useMemo(
      () => ({
        activeGroup,
        activeGroupId,
        setActiveGroupId: setActiveGroupIdMemo,
      }),
      [activeGroup, activeGroupId, setActiveGroupIdMemo],
    );

    return <Component {...(props as P)} {...injectedProps} />;
  });

  WrappedComponent.displayName = `withActiveGroup(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
